// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import axios from 'axios';

import { Arpaymentmethods } from '@gql/base-types';
import { useGetPaymentMethodsLazyQuery } from '../queries/getPaymentMethods.query.generated';
import { useGetArchargesForInvoiceLazyQuery } from '../queries/getArchargesForInvoice.query.generated';
import { useRooftop, useUser } from '@store';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { LinearProgress, Typography, useTheme } from '@mui/material';

import Loading from '@components/Utils/Loading';
import Spacer from '@components/Utils/Spacer';
import Success from '@components/Utils/Success';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';

import PayNowContent from './PayNowContent';
import { getPayNowAmount } from '../services/payNowAmount';

// TYPES ---------------------------------------------------------------- //

type PayNowModalProps = {
  input?: any;
  open?: boolean;
  onClose?: () => void;
  refetch?: () => void;
};

// COMPONENT ---------------------------------------------------------------- //

const PayNowModal = ({ input, open, onClose, refetch }: PayNowModalProps) => {
  const theme = useTheme();

  const user = useUser();
  const rooftop = useRooftop();

  const [formState, setFormState] = React.useState<string>(`pay-now`); // 'pay-now', 'pay-processing' or 'pay-success'
  // A card charge settles at once; a bank debit is accepted now and settles over the next few
  // business days. The success screen says which, so nobody reads "approved" as "the money moved".
  const [settlement, setSettlement] = React.useState<`settled` | `pending`>(`settled`);

  // Only the charge itself holds: a reload mid-request leaves the dealer unsure it went through.
  const holdsUnsavedWork = formState === `pay-processing`;
  useReloadGuard(holdsUnsavedWork, 'Payment');

  const [loading, setLoading] = React.useState<boolean>(false);
  const [internalError, setInternalError] = React.useState<boolean>(false);
  const [internalErrorText, setInternalErrorText] = React.useState<string>(``);

  const [paymentMethods, setPaymentMethods] = React.useState<Arpaymentmethods[]>([]);
  const [paymentMethod, setPaymentMethod] = React.useState<Arpaymentmethods | undefined>(undefined);
  const [archargeIds, setArchargeIds] = React.useState<number[]>([]);
  // sdk-ar's amount to charge for the invoice's charges, null until it loads.
  const [amountToCharge, setAmountToCharge] = React.useState<number | null>(null);

  const [getPaymentMethods] = useGetPaymentMethodsLazyQuery();
  // No cache: on a large invoice every id would be written into Apollo's cache as its own
  // entry, and a later open could be handed a stale list after the invoice's charges change.
  const [getArcharges] = useGetArchargesForInvoiceLazyQuery({ fetchPolicy: `no-cache` });

  React.useEffect(() => {
    const fetchData = async () => {
      if (!open || !input?.invoice?.id) return;

      setLoading(true);

      try {
        // Fetch payment methods
        const pmRes = await getPaymentMethods({ variables: { customerId: rooftop?.id || 0 } });
        const resPaymentMethods: Arpaymentmethods[] = pmRes?.data?.arpaymentmethods || [];
        // Default to the first method Stripe can charge; a row saved under the previous
        // processor stays listed (greyed) but is never preselected.
        const defaultPaymentMethod: Arpaymentmethods | undefined =
          resPaymentMethods.find(m => m.gateway === `stripe`) || undefined;

        setPaymentMethods(resPaymentMethods);
        setPaymentMethod(defaultPaymentMethod);

        // Every active charge on the invoice, whatever its type. The payment path
        // works out what each still owes, after credits and settled payments, so
        // the modal passes them all rather than guessing which are paid.
        setAmountToCharge(null);
        const chargesRes = await getArcharges({ variables: { invoiceId: input.invoice.id } });
        const chargeIds = (chargesRes?.data?.archarges || []).map((c: any) => Number(c.id));
        setArchargeIds(chargeIds);
        setAmountToCharge(await getPayNowAmount(chargeIds, Number(input.due || 0)));

        setInternalError(false);
        setInternalErrorText(``);
      } catch (err) {
        console.error(`Failed to fetch payment data:`, err);
        setInternalError(true);
        setInternalErrorText(`Failed to fetch payment data.`);
      }

      setLoading(false);
    };

    fetchData();
  }, [open, input?.invoice?.id]);

  const handleClose = () => {
    setFormState(`pay-now`);
    setInternalError(false);
    setInternalErrorText(``);
    if (refetch) refetch();
    if (onClose) onClose();
  };

  const handlePaymentMethodChange = (method: Arpaymentmethods | undefined) => {
    setInternalError(false);
    setInternalErrorText(``);
    setPaymentMethod(method);
  };

  // Every invoice pays the same way: its charges go to processPaymentCharges,
  // which pays them through sdk-ar applyPayment.
  const handleProcessPayment = async () => {
    try {
      const userToken = await user?.getToken();

      if (!userToken) {
        console.error(`Error getting user authentication. If this issue persists, please contact support.`);
        setInternalError(true);
        setInternalErrorText(`Error getting user authentication. If this issue persists, please contact support.`);
        return;
      }

      if (!paymentMethod) {
        console.error(`Please select a valid payment method to continue.`);
        setInternalError(true);
        setInternalErrorText(`Please select a valid payment method to continue.`);
        return;
      }

      setFormState(`pay-processing`);

      const variables = {
        archargeIds: archargeIds,
        amount: input.due || 0,
        callMethod: `dealer-pay-button`,
        paymentMethod: paymentMethod,
        invoiceId: input?.invoice?.id,
      };

      const res = await axios({
        method: `POST`,
        url: `/.netlify/functions/processPaymentCharges`,
        data: variables,
        headers: {
          authorization: `Bearer ${userToken}`,
        },
      });

      if (res?.status === 200) {
        setSettlement(res?.data?.settlement === `pending` ? `pending` : `settled`);
        setFormState(`pay-success`);
      } else {
        console.error(`Payment failed to process:`, res?.data);
        setInternalError(true);
        setInternalErrorText(`Payment failed to process! If this issue persists, please contact support.`);
        setFormState(`pay-now`);
      }
    } catch (err) {
      console.error(`An error occurred when attempting to post payment:`, err);
      setInternalError(true);
      setInternalErrorText(`Payment failed to process! If this issue persists, please contact support.`);
      setFormState(`pay-now`);
    }
  };

  return (
    <Modal open={open || false}>
      {open ? (
        <>
          {formState === `pay-now` && input ? (
            <>
              <ModalHeader handleClose={handleClose}>Pay Now</ModalHeader>

              <ModalContent
                subtitle={`By clicking "Make Payment", your method of payment will be charged for the amount displayed as the "Amount Due". If there are any disputed moves, the invoice will be considered partially paid until an agreement is reached.`}
              >
                {internalError && internalErrorText ? (
                  <>
                    <Typography style={{ marginBottom: `8px`, fontWeight: 500, color: theme?.palette?.error?.main }}>
                      {internalErrorText}
                    </Typography>

                    <Spacer size={1} />
                  </>
                ) : null}

                {loading ? <Loading /> : null}

                <PayNowContent
                  input={input}
                  amountToCharge={amountToCharge}
                  paymentMethods={paymentMethods}
                  paymentMethod={paymentMethod}
                  onPaymentMethodChange={handlePaymentMethodChange}
                />
              </ModalContent>

              <ModalFooter>
                <ModalAction
                  onClick={handleProcessPayment}
                  disabled={!paymentMethod || amountToCharge == null}
                >
                  Make Payment
                </ModalAction>
              </ModalFooter>
            </>
          ) : null}

          {formState === `pay-processing` ? (
            <>
              <ModalHeader>Processing Payment...</ModalHeader>

              <ModalContent>
                <LinearProgress />
              </ModalContent>
            </>
          ) : null}

          {formState === `pay-success` ? (
            <>
              <ModalHeader handleClose={handleClose}>
                {settlement === `pending` ? `Payment Submitted` : `Payment Approved!`}
              </ModalHeader>

              <ModalContent>
                <Success />
              </ModalContent>

              <ModalFooter>
                <ModalAction onClick={() => handleClose()} color='utility'>
                  Done
                </ModalAction>
              </ModalFooter>
            </>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default PayNowModal;
